import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared logic for TabCarousel component (GL2025 theme)
 * Used by both web and native implementations
 */

// Configuration for hover-to-scroll behavior
const HOVER_SCROLL_CONFIG = {
    initialDelay: 150,      // ms before scrolling starts
    scrollInterval: 16,     // ms between scroll steps (~60fps)
    initialSpeed: 2,        // initial pixels per step
    maxSpeed: 8,            // max pixels per step after acceleration
    accelerationTime: 1000  // ms to reach max speed
};

/**
 * Calculate gradient visibility based on scroll position
 * @param {number} scrollLeft - Current scroll position
 * @param {number} scrollWidth - Total scrollable width (content width)
 * @param {number} clientWidth - Visible viewport width
 * @returns {{showLeftGradient: boolean, showRightGradient: boolean}}
 */
export function calculateGradientVisibility(scrollLeft, scrollWidth, clientWidth) {
    // Show left gradient if scrolled from start
    const showLeftGradient = scrollLeft > 0;
    
    // Show right gradient if content extends beyond viewport
    // Use -1 for rounding tolerance
    const showRightGradient = scrollLeft + clientWidth < scrollWidth - 1;
    
    return { showLeftGradient, showRightGradient };
}

/**
 * Custom hook for managing scroll-based gradient visibility
 * @param {number} containerWidth - Width of the scroll container
 * @param {number} totalTabsWidth - Total width of all tabs
 * @returns {[object, function]} - [gradientState, handleScroll]
 */
export function useGradientVisibility(containerWidth, totalTabsWidth) {
    const [gradientState, setGradientState] = useState({
        showLeftGradient: false,
        showRightGradient: false
    });
    
    // Initial check - show right gradient if content overflows
    useEffect(() => {
        // Only update if we have valid dimensions
        if (containerWidth > 0 && totalTabsWidth > 0) {
            const shouldShowRight = totalTabsWidth > containerWidth;
            setGradientState(prev => {
                // Only update if value actually changed to avoid unnecessary renders
                if (prev.showRightGradient !== shouldShowRight) {
                    return {
                        showLeftGradient: false,
                        showRightGradient: shouldShowRight
                    };
                }
                return prev;
            });
        }
    }, [containerWidth, totalTabsWidth]);
    
    const handleScroll = useCallback((scrollLeft, scrollWidth, clientWidth) => {
        const visibility = calculateGradientVisibility(scrollLeft, scrollWidth, clientWidth);
        setGradientState(visibility);
    }, []);
    
    return [gradientState, handleScroll];
}

/**
 * Custom hook for hover-to-scroll functionality on gradient overlays
 * @param {React.RefObject} scrollContainerRef - Ref to the scroll container element
 * @returns {object} - Hover states and event handlers for left/right overlays
 */
export function useHoverScroll(scrollContainerRef) {
    const [isHoveringLeft, setIsHoveringLeft] = useState(false);
    const [isHoveringRight, setIsHoveringRight] = useState(false);

    // Refs for managing scroll animation
    const scrollIntervalRef = useRef(null);
    const delayTimeoutRef = useRef(null);
    const startTimeRef = useRef(null);

    // Clean up any running timers/intervals
    const cleanupScrolling = useCallback(() => {
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
            delayTimeoutRef.current = null;
        }
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }
        startTimeRef.current = null;
    }, []);

    // Start scrolling in a direction with acceleration
    const startScrolling = useCallback((direction) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        cleanupScrolling();

        // Delay before scrolling starts to prevent accidental triggers
        delayTimeoutRef.current = setTimeout(() => {
            startTimeRef.current = Date.now();

            scrollIntervalRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                const accelerationProgress = Math.min(elapsed / HOVER_SCROLL_CONFIG.accelerationTime, 1);

                // Ease-out acceleration curve
                const speedRange = HOVER_SCROLL_CONFIG.maxSpeed - HOVER_SCROLL_CONFIG.initialSpeed;
                const currentSpeed = HOVER_SCROLL_CONFIG.initialSpeed + (speedRange * accelerationProgress);

                const scrollAmount = direction === 'left' ? -currentSpeed : currentSpeed;
                container.scrollLeft += scrollAmount;
            }, HOVER_SCROLL_CONFIG.scrollInterval);
        }, HOVER_SCROLL_CONFIG.initialDelay);
    }, [scrollContainerRef, cleanupScrolling]);

    // Event handlers for left overlay
    const handleLeftMouseEnter = useCallback(() => {
        setIsHoveringLeft(true);
        startScrolling('left');
    }, [startScrolling]);

    const handleLeftMouseLeave = useCallback(() => {
        setIsHoveringLeft(false);
        cleanupScrolling();
    }, [cleanupScrolling]);

    // Event handlers for right overlay
    const handleRightMouseEnter = useCallback(() => {
        setIsHoveringRight(true);
        startScrolling('right');
    }, [startScrolling]);

    const handleRightMouseLeave = useCallback(() => {
        setIsHoveringRight(false);
        cleanupScrolling();
    }, [cleanupScrolling]);

    // Cleanup on unmount
    useEffect(() => {
        return cleanupScrolling;
    }, [cleanupScrolling]);

    return {
        isHoveringLeft,
        isHoveringRight,
        handleLeftMouseEnter,
        handleLeftMouseLeave,
        handleRightMouseEnter,
        handleRightMouseLeave
    };
}

/**
 * Get gradient styles based on theme
 * @param {object} theme - Theme object (already mode-specific)
 * @param {string} direction - 'left' or 'right'
 * @returns {object} Gradient style configuration
 */
export function getGradientStyles(theme, direction) {
    // Get gradient configuration from pre-computed theme.tabs
    const gradientConfig = theme.tabs?.gradient;
    return {
        width: gradientConfig.width,
        height: gradientConfig.height,
        background: direction === 'left' 
            ? gradientConfig.left.background 
            : gradientConfig.right.background
    };
}

function parseSize(value) {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string') {
        const parsedValue = Number.parseFloat(value);
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    return 0;
}

function getShadowSpread(value) {
    if (typeof value !== 'string') {
        return 0;
    }

    return value
        .split(',')
        .map(shadow => shadow.trim().split(/\s+/))
        .reduce((maxSpread, parts) => {
            if (parts.length < 4) {
                return maxSpread;
            }

            return Math.max(maxSpread, parseSize(parts[3]));
        }, 0);
}

function getTabStateConfig(theme) {
    return [
        theme?.tabs?.current?.default,
        theme?.tabs?.current?.focus,
        theme?.tabs?.inactive?.default,
        theme?.tabs?.inactive?.focus,
        theme?.tabs?.disabled
    ].filter(Boolean);
}

function getTabVisualHeight(tabState) {
    const container = tabState?.container;
    const text = tabState?.text;
    const paddingTop = parseSize(container?.paddingTop);
    const paddingBottom = parseSize(container?.paddingBottom);
    const borderWidth = parseSize(container?.borderWidth);
    const borderBottomWidth = parseSize(container?.borderBottomWidth);
    const borderTopWidth = borderWidth;
    const explicitHeight = parseSize(container?.height);
    const contentHeight = parseSize(text?.lineHeight);
    if (explicitHeight > 0) {
        return explicitHeight;
    }

    return contentHeight + paddingTop + paddingBottom + borderTopWidth + Math.max(borderWidth, borderBottomWidth);
}

export function getTabBarMinHeight(theme) {
    const containerConfig = theme?.tabs?.container;
    const tabStates = getTabStateConfig(theme);
    const tallestTab = tabStates.reduce((maxHeight, tabState) => {
        return Math.max(maxHeight, getTabVisualHeight(tabState));
    }, 0);
    const containerPaddingTop = parseSize(containerConfig?.paddingTop);
    const containerBorderWidth = parseSize(containerConfig?.borderWidth);
    const containerBorderTopWidth = Math.max(containerBorderWidth, parseSize(containerConfig?.borderTopWidth));
    const containerBorderBottomWidth = Math.max(containerBorderWidth, parseSize(containerConfig?.borderBottomWidth));
    const focusShadowSpread = tabStates.reduce((maxSpread, tabState) => {
        return Math.max(maxSpread, getShadowSpread(tabState?.container?.boxShadow));
    }, 0);

    return Math.ceil(
        containerPaddingTop +
        tallestTab +
        focusShadowSpread +
        containerBorderTopWidth +
        containerBorderBottomWidth
    );
}

/**
 * Calculate total width of all tabs
 * @param {Array} childrenWidths - Array of individual tab widths
 * @param {number} spacing - Spacing between tabs (default: 12, stretch: 32)
 * @returns {number} Total width including spacing
 */
export function calculateTotalTabsWidth(childrenWidths, spacing = 12) {
    if (!childrenWidths || childrenWidths.length === 0) return 0;
    
    const tabsWidth = childrenWidths.reduce((sum, width) => sum + width, 0);
    const spacingWidth = (childrenWidths.length - 1) * spacing;
    
    return tabsWidth + spacingWidth;
}

/**
 * Get spacing value based on spacing mode
 * @param {string} spacingMode - 'default' or 'stretch'
 * @param {object} theme - Theme object (already mode-specific)
 * @returns {number} Spacing value in pixels
 */
export function getSpacingValue(spacingMode, theme) {
    // Use pre-computed theme.tabs.spacing
    const spacing = theme.tabs?.spacing;
    
    if (!spacing) {
        return spacingMode === 'stretch' ? 32 : 12;
    }
    
    return spacingMode === 'stretch' ? spacing.stretch : spacing.default;
}

/**
 * Check if content overflows container (needs scrolling)
 * @param {number} totalWidth - Total width of content
 * @param {number} containerWidth - Width of container
 * @returns {boolean} Whether content overflows
 */
export function contentOverflows(totalWidth, containerWidth) {
    return totalWidth > containerWidth;
}

/**
 * Scroll to selected tab (ensure it's visible)
 * @param {HTMLElement} containerElement - Scroll container element
 * @param {HTMLElement} selectedTabElement - Selected tab element
 * @param {number} scrollPadding - Padding from edges (default: 24)
 */
export function scrollToTab(containerElement, selectedTabElement, scrollPadding = 24) {
    if (!containerElement || !selectedTabElement) return;
    
    const containerRect = containerElement.getBoundingClientRect();
    const tabRect = selectedTabElement.getBoundingClientRect();
    
    const tabLeft = tabRect.left - containerRect.left + containerElement.scrollLeft;
    const tabRight = tabLeft + tabRect.width;
    
    const viewportLeft = containerElement.scrollLeft;
    const viewportRight = viewportLeft + containerRect.width;
    
    // If tab is to the left of viewport, scroll left
    if (tabLeft < viewportLeft + scrollPadding) {
        containerElement.scrollTo({
            left: Math.max(0, tabLeft - scrollPadding),
            behavior: 'smooth'
        });
    }
    // If tab is to the right of viewport, scroll right
    else if (tabRight > viewportRight - scrollPadding) {
        containerElement.scrollTo({
            left: tabRight - containerRect.width + scrollPadding,
            behavior: 'smooth'
        });
    }
}
